import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  LayoutDashboard, Users, TrendingUp, BarChart3, Package,
  Settings, Mail, Cpu, FileCode, ChevronDown, ChevronRight, Briefcase, UserCog, Building2, Bell, BookOpen, UserPlus, Database, GitBranch, Megaphone, Crosshair
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertBellButton, AlertModal } from './AlertSystem';
import * as api from '../services/api';

const Sidebar = ({ collapsed = false }) => {
  const { state, dispatch } = useData();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [campaignsOpen, setCampaignsOpen] = React.useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  // Get current user role
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin';
  const isViewer = currentUser.role === 'viewer';
  const isTrafficker = currentUser.role === 'trafficker';
  const canSeeAllLeads = isAdmin || isViewer;

  // Load unread alerts count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const alertsArray = await api.getAlerts();
        const unread = (alertsArray || []).filter(a => !a.isRead).length;
        setUnreadAlerts(unread);
      } catch (error) {
        console.error('Error loading alerts count:', error);
      }
    };
    loadUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Main menu items visible to all users
  const mainMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Users, label: canSeeAllLeads ? 'Todos los Leads' : 'Mis Leads', path: '/leads' },
    { icon: Building2, label: 'Empresas', path: '/empresas' },
    { icon: BarChart3, label: 'Estadísticas', path: '/statistics' },
    { icon: Crosshair, label: 'Trafficker', path: '/trafficker' },
    { icon: BarChart3, label: 'Comportamientos', path: '/trafficker/comportamientos' },
    { icon: Package, label: 'Servicios', path: '/services' },
  ];

  // Admin-only menu items (in main section)
  const adminOnlyMainItems = [
    { icon: TrendingUp, label: 'FormBuilder', path: '/form-builder' },
    { icon: UserPlus, label: 'Referidores', path: '/referidos' },
  ];

  const campaignMenuItems = [
    { icon: Megaphone, label: 'Mis Campañas', path: '/campaigns/mis-campanas' },
    { icon: Megaphone, label: 'Google Ads', path: '/campaigns/adwords' },
    { icon: Megaphone, label: 'Facebook Ads', path: '/campaigns/facebook' },
  ];

  // Full admin menu
  const adminMenuItems = [
    { icon: Settings, label: 'Configuración General', path: '/admin/settings' },
    { icon: GitBranch, label: 'Gestión Embudos', path: '/pipelines' },
    { icon: UserCog, label: 'Usuarios', path: '/admin/users' },
    { icon: Database, label: 'Datos de Tracking', path: '/admin/tracking' },
    { icon: Mail, label: 'Configuración Email', path: '/admin/email' },
    { icon: Cpu, label: 'Integración IA', path: '/admin/ai-integration' },
    { icon: FileCode, label: 'API Docs', path: '/admin/api-docs' },
    { icon: BookOpen, label: 'Reglas de Negocio', path: '/admin/business-rules' },
  ];

  // Commercial user menu (only their profile)
  const commercialMenuItems = [
    { icon: UserCog, label: 'Mi Perfil', path: '/admin/users' },
  ];

  // Use dynamic colors from settings
  const primaryColor = settings?.primaryColor || '#4f46e5';

  // Trafficker role: restricted sidebar
  if (isTrafficker) {
    const traffMenuItems = [
      { icon: Users, label: 'Todos los Leads', path: '/leads' },
      { icon: Crosshair, label: 'Trafficker', path: '/trafficker' },
      { icon: BarChart3, label: 'Comportamientos', path: '/trafficker/comportamientos' },
    ];
    return (
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen transition-all duration-300">
        <div className="p-5 border-b border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => navigate('/trafficker')}>
          {settings?.logoUrl ? (
            <img src={settings.logoUrl} alt={settings?.appName || 'Logo'} className="h-12 w-auto object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-2xl font-bold tracking-wide" style={{ color: primaryColor }}>DATALAB</span>
            </div>
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {traffMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                style={isActive ? { backgroundColor: primaryColor, color: 'white' } : {}}>
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <span className="text-white text-sm font-medium">{currentUser.name?.[0] || 'T'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{currentUser.name || 'Trafficker'}</p>
              <p className="text-xs text-slate-500 truncate">Trafficker</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (collapsed) {
    return null;
  }

  return (
    <div className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen transition-all duration-300">
      {/* Logo - Clickable to go to Dashboard */}
      <div 
        className="p-5 border-b border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => navigate('/')}
      >
        {settings?.logoUrl ? (
          <img 
            src={settings.logoUrl} 
            alt={settings?.appName || 'Logo'} 
            className="h-12 w-auto object-contain"
          />
        ) : (
          <div className="flex items-center gap-2">
            {/* Default DataLab-style logo */}
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-2xl font-bold tracking-wide" style={{ color: primaryColor }}>
              DATALAB
            </span>
          </div>
        )}
      </div>

      {/* Alert Button */}
      <div className="px-4 py-3 border-b border-slate-200">
        <button
          onClick={() => setShowAlertModal(true)}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="text-sm font-medium text-slate-700">Alertas</span>
          </div>
          {unreadAlerts > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">
              {unreadAlerts > 99 ? '99+' : unreadAlerts}
            </span>
          )}
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {mainMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              style={isActive ? { backgroundColor: primaryColor, color: 'white' } : {}}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium text-sm">{item.label}</span>
            </button>
          );
        })}

        {/* Admin-only items in main section (Referidores) */}
        {isAdmin && adminOnlyMainItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              style={isActive ? { backgroundColor: primaryColor, color: 'white' } : {}}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium text-sm">{item.label}</span>
            </button>
          );
        })}

        {/* Campaigns Section - Admin only */}
        {isAdmin && (
          <div className="pt-4 mt-2 border-t border-slate-200">
            <button
              onClick={() => setCampaignsOpen(!campaignsOpen)}
              className="w-full flex items-center justify-between px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Megaphone className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium text-sm">Campañas</span>
              </div>
              {campaignsOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            
            {campaignsOpen && (
              <div className="ml-4 mt-1 space-y-1">
                {campaignMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                        isActive
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pipelines Section */}
        <div className="pt-4 mt-2 border-t border-slate-200">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 mb-2">
            Embudos
          </p>
          {state.pipelines.map((pipeline) => {
            const isActive = location.pathname.includes(pipeline.id);
            return (
              <button
                key={pipeline.id}
                onClick={() => {
                  dispatch({ type: 'SET_SELECTED_PIPELINE', payload: pipeline.id });
                  navigate(`/pipeline/${pipeline.id}`);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                style={isActive ? { backgroundColor: primaryColor, color: 'white' } : {}}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: pipeline.stages?.[0]?.color || primaryColor }}
                />
                <span className="text-sm font-medium">{pipeline.name}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Admin Section - Different menu for admin vs commercial */}
      <div className="border-t border-slate-200">
        <button
          onClick={() => setAdminOpen(!adminOpen)}
          className="w-full flex items-center justify-between px-6 py-3 text-slate-600 hover:bg-slate-50"
        >
          <span className="text-xs font-semibold uppercase tracking-wider">
            {isAdmin ? 'Administración' : 'Mi Cuenta'}
          </span>
          {adminOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        
        {adminOpen && (
          <div className="px-4 pb-4 space-y-1">
            {(isAdmin ? adminMenuItems : commercialMenuItems).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                    isActive
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: primaryColor }}
          >
            <span className="text-white text-sm font-medium">{currentUser.name?.[0] || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{currentUser.name || 'Usuario'}</p>
            <p className="text-xs text-slate-500 truncate">{isAdmin ? 'Administrador' : currentUser.role === 'comercial' ? 'Comercial' : currentUser.role || 'Usuario'}</p>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
      />
    </div>
  );
};

export default Sidebar;
